import React from 'react';

import { Alert } from '@mui/material';

import ApprovalQueue from './ApprovalQueue';
import PlaceholderChecklistTab from './PlaceholderChecklistTab';
import { isRowApproved } from './approvalQueueModel';
import BlackoutReportCard from './tabs/blackoutDates/BlackoutReportCard';
import type { BlackoutCoverageSummary } from './tabs/blackoutDates/types';
import BundledExperiencesReportCard from './tabs/bundledExperiences/BundledExperiencesReportCard';
import CampaignReportCard from './tabs/campaign/CampaignReportCard';
import DatesRestrictionsReportCard from './tabs/datesRestrictions/DatesRestrictionsReportCard';
import DealOptionsInclusionsReportCard from './tabs/dealOptionsInclusions/DealOptionsInclusionsReportCard';
import type { DealOptionsInclusionsDealRow } from './tabs/dealOptionsInclusions/types';
import CapacityReportCard from './tabs/egs/CapacityReportCard';
import MarginReportCard from './tabs/egs/MarginReportCard';
import SetupReportCard from './tabs/setup/SetupReportCard';
import SurchargesReportCard from './tabs/surcharges/SurchargesReportCard';
import TaxesPaymentsReportCard from './tabs/taxesPayments/TaxesPaymentsReportCard';
import type {
  ApprovalQueueItem,
  ApprovalReportSummary,
  ChecklistCategory,
  ChecklistRowsByCategory,
  ManualApprovalState,
} from './types';
import type { VisibleChecklistRows } from './useVisibleChecklistRows';

interface Props {
  activeCategory: ChecklistCategory;
  blackoutCoverage: BlackoutCoverageSummary;
  dealOptionsInclusionsDealRows: Array<DealOptionsInclusionsDealRow>;
  expandedQueueItems: Record<string, boolean>;
  isApprovalQueueMode: boolean;
  manualApprovals: ManualApprovalState;
  needsApprovalCount: number;
  opportunityError?: unknown;
  reportSummaries: Array<ApprovalReportSummary>;
  rows: ChecklistRowsByCategory;
  showAutoApproved: boolean;
  travelFrom?: string;
  travelTo?: string;
  visibleQueueItems: Array<ApprovalQueueItem>;
  visibleRows: VisibleChecklistRows;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (itemId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function ChecklistActiveTab({
  activeCategory,
  blackoutCoverage,
  dealOptionsInclusionsDealRows,
  expandedQueueItems,
  isApprovalQueueMode,
  manualApprovals,
  needsApprovalCount,
  opportunityError,
  reportSummaries,
  rows,
  showAutoApproved,
  travelFrom,
  travelTo,
  visibleQueueItems,
  visibleRows,
  onApprovalChange,
  onToggleDetails,
  onUnapprovalChange,
}: Props) {
  if (isApprovalQueueMode) {
    return (
      <ApprovalQueue
        expandedQueueItems={expandedQueueItems}
        items={visibleQueueItems}
        needsApprovalCount={needsApprovalCount}
        onApprovalChange={onApprovalChange}
        onToggleDetails={onToggleDetails}
        reportSummaries={reportSummaries}
      />
    );
  }

  switch (activeCategory.id) {
    case 'setup':
      return (
        <SetupReportCard
          hiddenApprovedCount={rows.setupRows.filter((row) => isRowApproved(row, manualApprovals)).length}
          manualApprovals={manualApprovals}
          rows={visibleRows.visibleSetupRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={onApprovalChange}
        />
      );
    case 'campaign':
      return (
        <CampaignReportCard
          hiddenApprovedCount={rows.campaignRows.filter((row) => isRowApproved(row, manualApprovals)).length}
          manualApprovals={manualApprovals}
          rows={visibleRows.visibleCampaignRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={onApprovalChange}
        />
      );
    case 'datesRestrictions':
      return (
        <DatesRestrictionsReportCard
          hiddenApprovedCount={rows.datesRestrictionsRows.filter((row) => isRowApproved(row, manualApprovals)).length}
          manualApprovals={manualApprovals}
          rows={visibleRows.visibleDatesRestrictionsRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={onApprovalChange}
        />
      );
    case 'egs':
      return (
        <>
          <MarginReportCard
            hiddenApprovedCount={rows.marginRows.filter((row) => isRowApproved(row, manualApprovals)).length}
            manualApprovals={manualApprovals}
            rows={visibleRows.visibleMarginRows}
            showAutoApproved={showAutoApproved}
            onApprovalChange={onApprovalChange}
          />

          <CapacityReportCard
            hiddenApprovedCount={rows.capacityRows.filter((row) => isRowApproved(row, manualApprovals)).length}
            manualApprovals={manualApprovals}
            rows={visibleRows.visibleCapacityRows}
            showAutoApproved={showAutoApproved}
            onApprovalChange={onApprovalChange}
          />
        </>
      );
    case 'dealOptionsInclusions':
      return (
        <>
          {opportunityError && (
            <Alert severity="warning">
              Unable to load deal options or inclusions data — some rules may show as missing data.
            </Alert>
          )}
          <DealOptionsInclusionsReportCard
            dealRows={dealOptionsInclusionsDealRows}
            hiddenApprovedCount={
              rows.dealOptionsInclusionsRows.filter((row) => isRowApproved(row, manualApprovals)).length
            }
            manualApprovals={manualApprovals}
            showAutoApproved={showAutoApproved}
            onApprovalChange={onApprovalChange}
            onUnapprovalChange={onUnapprovalChange}
          />
        </>
      );
    case 'surcharges':
      return (
        <SurchargesReportCard
          flaggedRules={rows.baseSurchargesRows}
          hiddenApprovedCount={rows.surchargeTableRows.filter((row) => isRowApproved(row, manualApprovals)).length}
          manualApprovals={manualApprovals}
          rows={visibleRows.visibleSurchargeTableRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={onApprovalChange}
        />
      );
    case 'blackoutDates':
      return (
        <>
          {opportunityError && (
            <Alert severity="warning">
              Unable to load Salesforce opportunity blackout dates — affected rules require approval.
            </Alert>
          )}
          <BlackoutReportCard
            coverage={blackoutCoverage}
            hiddenApprovedCount={rows.blackoutRows.filter((row) => isRowApproved(row, manualApprovals)).length}
            manualApprovals={manualApprovals}
            rows={visibleRows.visibleBlackoutRows}
            showAutoApproved={showAutoApproved}
            travelFrom={travelFrom}
            travelTo={travelTo}
            onApprovalChange={onApprovalChange}
          />
        </>
      );
    case 'bundledExperiences':
      return (
        <BundledExperiencesReportCard
          hiddenApprovedCount={rows.bundledExperienceRows.filter((row) => isRowApproved(row, manualApprovals)).length}
          manualApprovals={manualApprovals}
          rows={visibleRows.visibleBundledExperienceRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={onApprovalChange}
        />
      );
    case 'taxesPayments':
      return (
        <>
          {opportunityError && (
            <Alert severity="warning">
              Unable to load Salesforce opportunity data — some rules may show as missing data.
            </Alert>
          )}
          <TaxesPaymentsReportCard
            expandedRows={expandedQueueItems}
            hiddenApprovedCount={rows.taxesPaymentsRows.filter((row) => isRowApproved(row, manualApprovals)).length}
            manualApprovals={manualApprovals}
            rows={visibleRows.visibleTaxesPaymentsRows}
            showAutoApproved={showAutoApproved}
            onApprovalChange={onApprovalChange}
            onToggleDetails={onToggleDetails}
            onUnapprovalChange={onUnapprovalChange}
          />
        </>
      );
    default:
      return <PlaceholderChecklistTab category={activeCategory} />;
  }
}
